/**
 * AccountPage - Enhanced account settings with profile information
 *
 * Shows user profile data from Stripe integration
 * Displays premium status if applicable
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { uploadAvatar } from '@/services/uploadService'
import { SubscriptionOnboardingModal } from '@/components/auth/SubscriptionOnboardingModal'
import { api } from '@/services/api'
import { useMailingAddress } from '@/features/mailing/useMailingAddress'
import { AddressForm } from '@/features/mailing/AddressForm'
import { EnvelopeModal } from '@/features/mailing/EnvelopeModal'
import { Modal } from '@/components/ui/Modal'
import { updateAddress as updateMailingAddress, type AddressData } from '@/features/mailing/mailingService'
import { getPaymentMethods, deletePaymentMethod, setDefaultPaymentMethod } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import PaymentMethodCard from '@/features/wallet/PaymentMethodCard'
import AddPaymentMethodModal from '@/features/wallet/AddPaymentMethodModal'
import { useToast } from '@/components/ui/ToastContainer'
import { getIdentityStatus } from '@/api/identityVerification'
import { getDocumentStatus, type DocumentRecord } from '@/api/documents'
import { DocumentSigningModal } from '@/components/documents/DocumentSigningModal'
import {
  MASTER_AGREEMENT_TITLE, MASTER_AGREEMENT_TYPE, MASTER_AGREEMENT_VERSION, MASTER_AGREEMENT_SECTIONS,
  MEETUP_AGREEMENT_TITLE, MEETUP_AGREEMENT_TYPE, MEETUP_AGREEMENT_VERSION, MEETUP_AGREEMENT_SECTIONS,
} from '@/components/documents/agreementTexts'

export function AccountPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { showSuccess, showError } = useToast()
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatar_url || null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [smsNotifications, setSmsNotifications] = useState(true)
  const [isSavingPreferences, setIsSavingPreferences] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Trial banner state
  const [trialInfo, setTrialInfo] = useState<{ isOnTrial: boolean; daysLeft: number; hasPaymentMethod: boolean } | null>(null)

  // Wallet state
  const [walletBalance, setWalletBalance] = useState<number>(0)
  const [isLoadingWallet, setIsLoadingWallet] = useState(true)
  const [giftCardCode, setGiftCardCode] = useState('')
  const [isRedeeming, setIsRedeeming] = useState(false)
  const [redeemResult, setRedeemResult] = useState<{ success: boolean; message: string } | null>(null)
  // "Update Payment Method" lands here as /account?wallet=open: the wallet (saved cards and
  // "+ Add Card") starts open and is scrolled into view instead of hiding behind a collapsed header
  const openWalletFromLink = new URLSearchParams(window.location.search).get('wallet') === 'open'
  const [walletExpanded, setWalletExpanded] = useState(openWalletFromLink)
  const walletRef = useRef<HTMLDivElement>(null)
  const walletScrolledRef = useRef(false)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [isLoadingCards, setIsLoadingCards] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  // Mailing address state
  const { address: mailingAddress, hasAddress: hasMailingAddress, isLoading: isMailingLoading, refresh: refreshMailing } = useMailingAddress()
  const [showEnvelopeModal, setShowEnvelopeModal] = useState(false)
  const [showEditConfirm, setShowEditConfirm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false)
  const [pendingUpdate, setPendingUpdate] = useState<AddressData | null>(null)
  const [isUpdatingAddress, setIsUpdatingAddress] = useState(false)
  const [addressUpdateError, setAddressUpdateError] = useState<string | null>(null)

  // Documents state
  const [isIdentityVerified, setIsIdentityVerified] = useState(false)
  const [signedDocuments, setSignedDocuments] = useState<DocumentRecord[]>([])
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true)
  const [openDocumentType, setOpenDocumentType] = useState<string | null>(null)

  const handleEditAddress = () => {
    setShowEditConfirm(true)
  }

  const handleConfirmEdit = () => {
    setShowEditConfirm(false)
    setShowEditForm(true)
  }

  const handleEditFormSubmit = (data: AddressData) => {
    setPendingUpdate(data)
    setShowEditForm(false)
    setShowUpdateConfirm(true)
  }

  const handleConfirmUpdate = async () => {
    if (!pendingUpdate) return
    setIsUpdatingAddress(true)
    setAddressUpdateError(null)

    try {
      const result = await updateMailingAddress(pendingUpdate)
      if (result.validation_error) {
        setAddressUpdateError(result.reason || 'Address validation failed.')
        setShowUpdateConfirm(false)
        setShowEditForm(true)
        return
      }
      if (result.success) {
        setShowUpdateConfirm(false)
        setPendingUpdate(null)
        refreshMailing()
      }
    } catch (err) {
      setAddressUpdateError(err instanceof Error ? err.message : 'Failed to update address')
    } finally {
      setIsUpdatingAddress(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Fetch trial info for banner
  useEffect(() => {
    async function fetchTrialInfo() {
      if (!user) return

      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`${API_URL}/api/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          const sub = data.subscription

          if (sub?.status === 'trialing' && sub?.trial_end) {
            const trialEnd = new Date(sub.trial_end)
            const now = new Date()
            const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
            setTrialInfo({
              isOnTrial: true,
              daysLeft: Math.max(0, daysLeft),
              hasPaymentMethod: !!sub.default_payment_method
            })
          }
        }
      } catch (error) {
        console.error('Failed to fetch trial info:', error)
      }
    }

    fetchTrialInfo()
  }, [user])

  // Fetch identity verification + document signing status
  useEffect(() => {
    async function fetchDocumentInfo() {
      if (!user) return
      const token = localStorage.getItem('auth_token')
      if (!token) return

      try {
        const [identityRes, docsRes] = await Promise.all([
          getIdentityStatus(token),
          getDocumentStatus(token),
        ])
        setIsIdentityVerified(identityRes.verified)
        setSignedDocuments(docsRes.documents)
      } catch {
        // Silent fail — section just won't show
      } finally {
        setIsLoadingDocuments(false)
      }
    }
    fetchDocumentInfo()
  }, [user])

  const refreshDocumentStatus = async () => {
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const docsRes = await getDocumentStatus(token)
      setSignedDocuments(docsRes.documents)
    } catch {
      // ignore
    }
  }

  const isDocumentSigned = (docType: string) =>
    signedDocuments.some(d => d.type === docType)

  // Fetch notification preferences on mount
  useEffect(() => {
    async function fetchPreferences() {
      if (!user) return

      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
        const response = await fetch(`${API_URL}/api/customer/preferences/${user.id}`)

        if (response.ok) {
          const data = await response.json()
          setEmailNotifications(data.notifications_email !== false)
          setSmsNotifications(data.notifications_sms !== false)
        }
      } catch (error) {
        console.error('Failed to fetch preferences:', error)
      }
    }

    fetchPreferences()
  }, [user])

  // Fetch wallet balance on mount
  useEffect(() => {
    async function fetchWalletBalance() {
      if (!user) return

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`${api.baseURL}/api/wallet/balance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          setWalletBalance(data.balance || 0)
        }
      } catch (error) {
        console.error('Failed to fetch wallet balance:', error)
      } finally {
        setIsLoadingWallet(false)
      }
    }

    fetchWalletBalance()
  }, [user])

  // Fetch payment methods when wallet is expanded
  const fetchPaymentMethods = async () => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    try {
      setIsLoadingCards(true)
      const response = await getPaymentMethods(token)
      setPaymentMethods(response.payment_methods || [])
    } catch {
      // silently fail
    } finally {
      setIsLoadingCards(false)
    }
  }

  useEffect(() => {
    if (walletExpanded && paymentMethods.length === 0) {
      fetchPaymentMethods()
    }
  }, [walletExpanded])

  // Opened from "Update Payment Method": bring the wallet into view once it has rendered
  useEffect(() => {
    if (!openWalletFromLink || walletScrolledRef.current || !walletRef.current) return
    walletScrolledRef.current = true
    setTimeout(() => walletRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300)
  }, [user])

  const handleSetDefault = async (paymentMethodId: string) => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    try {
      await setDefaultPaymentMethod(token, paymentMethodId)
      showSuccess('Default payment method updated')
      await fetchPaymentMethods()
    } catch (error: any) {
      showError(error.message || 'Failed to set default')
      throw error
    }
  }

  const handleDeleteCard = async (paymentMethodId: string) => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    try {
      await deletePaymentMethod(token, paymentMethodId)
      showSuccess('Card removed')
      await fetchPaymentMethods()
    } catch (error: any) {
      showError(error.message || 'Failed to delete card')
      throw error
    }
  }

  const handleAddCardSuccess = () => {
    showSuccess('Card added successfully!')
    fetchPaymentMethods()
  }

  // Handle gift card redemption
  const handleRedeemGiftCard = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!giftCardCode.trim() || isRedeeming) return

    setIsRedeeming(true)
    setRedeemResult(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${api.baseURL}/api/wallet/redeem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: giftCardCode.trim() })
      })

      const data = await response.json()

      if (response.ok) {
        setRedeemResult({ success: true, message: data.message })
        setWalletBalance(data.newBalance)
        setGiftCardCode('')
      } else {
        setRedeemResult({ success: false, message: data.error || 'Failed to redeem gift card' })
      }
    } catch (error) {
      console.error('Failed to redeem gift card:', error)
      setRedeemResult({ success: false, message: 'Failed to redeem gift card. Please try again.' })
    } finally {
      setIsRedeeming(false)
    }
  }

  // Handle notification preference toggle
  const handleTogglePreference = async (type: 'email' | 'sms', value: boolean) => {
    if (!user) return

    try {
      setIsSavingPreferences(true)

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/customer/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          [type === 'email' ? 'notifications_email' : 'notifications_sms']: value
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update preferences')
      }

      // Update local state
      if (type === 'email') {
        setEmailNotifications(value)
      } else {
        setSmsNotifications(value)
      }

    } catch (error) {
      console.error('Failed to update preferences:', error)
      alert('Failed to update notification preferences')
    } finally {
      setIsSavingPreferences(false)
    }
  }

  // Handle avatar upload
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !user) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a JPG, PNG, WEBP, or GIF image')
      return
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      setUploadError('Image must be under 5MB')
      return
    }

    try {
      setIsUploading(true)
      setUploadError(null)

      // Create preview
      const previewUrl = URL.createObjectURL(file)
      setAvatarPreview(previewUrl)

      // Upload to S3 via backend
      const avatarUrl = await uploadAvatar(file, user.id)

      // Save avatar URL to database
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/customer/update-avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          avatarUrl: avatarUrl
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save avatar to profile')
      }

      // Update user object in localStorage so it persists
      const updatedUser = { ...user, avatar_url: avatarUrl }
      localStorage.setItem('user_data', JSON.stringify(updatedUser))

      // Update preview with actual URL
      setAvatarPreview(avatarUrl)
    } catch (err) {
      console.error('Avatar upload error:', err)
      setUploadError(err instanceof Error ? err.message : 'Failed to upload avatar')
      setAvatarPreview(user.avatar_url || null) // Revert to old avatar
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Free Trial Banner */}
        {trialInfo?.isOnTrial && !trialInfo.hasPaymentMethod && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 animate-slide-up">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏰</span>
              <div className="flex-1">
                <p className="font-semibold text-yellow-800">
                  Free Trial - {trialInfo.daysLeft} days left
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Add a payment method to keep your access after your trial ends.
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  className="mt-3 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Add Payment Method
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Account Card */}
        <div className="bg-white rounded-lg p-8 shadow-lg animate-slide-up">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Account Settings
            </h1>
          </div>

          {/* Avatar Section */}
          {user && (
            <div className="flex flex-col items-center mb-6">
              {/* Avatar Circle */}
              <div className="relative mb-3">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Profile avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-5xl">👤</div>
                  )}
                </div>
                {isUploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg,image/gif"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : avatarPreview ? 'Change Photo' : 'Upload Photo'}
              </button>

              {/* Upload Error */}
              {uploadError && (
                <p className="mt-2 text-xs text-red-600">{uploadError}</p>
              )}
            </div>
          )}

          {/* Profile Information Section */}
          {user && (
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  Profile Information
                </h2>

                {/* First Name */}
                {user.firstName && (
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900">
                      {user.firstName}
                    </div>
                  </div>
                )}

                {/* Last Name */}
                {user.lastName && (
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900">
                      {user.lastName}
                    </div>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-800">
                    {user.email}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mailing Address Section */}
          {user && (
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  Mailing Address
                </h2>

                {isMailingLoading ? (
                  <div className="h-16 bg-gray-200 rounded animate-pulse"></div>
                ) : hasMailingAddress && mailingAddress ? (
                  <div className="p-3 bg-white border border-gray-200 rounded-md">
                    <p className="text-sm font-medium text-gray-900">{mailingAddress.full_name}</p>
                    <p className="text-sm text-gray-700 mt-1">{mailingAddress.address_line1}</p>
                    {mailingAddress.address_line2 && (
                      <p className="text-sm text-gray-700">{mailingAddress.address_line2}</p>
                    )}
                    <p className="text-sm text-gray-700">
                      {mailingAddress.city}, {mailingAddress.state} {mailingAddress.zip_code}
                    </p>
                    {mailingAddress.status === 'pending' && (
                      <p className="text-xs text-amber-600 mt-2">Pending review</p>
                    )}
                    <button
                      onClick={handleEditAddress}
                      className="mt-2 text-xs text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      Edit Address
                    </button>
                  </div>
                ) : (
                  <div className="text-center p-4">
                    <p className="text-sm text-gray-700 mb-2">No mailing address on file</p>
                    <button
                      onClick={() => setShowEnvelopeModal(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors"
                    >
                      Add Address
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Wallet Section */}
          {user && (
            <div className="space-y-4 mb-6">
              <div ref={walletRef} className="bg-gray-50 rounded-lg p-5 overflow-hidden">
                {/* Wallet header - clickable to expand */}
                <button
                  onClick={() => setWalletExpanded(!walletExpanded)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-bold text-gray-900">Wallet</h2>
                    {!isLoadingWallet && walletBalance > 0 && (
                      <span className="text-base font-bold text-green-600">${walletBalance.toFixed(2)}</span>
                    )}
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-600 transition-transform ${walletExpanded ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Expanded wallet content */}
                {walletExpanded && (
                  <div className="mt-4 space-y-4">
                    {/* Balance Display - only show if balance > 0 */}
                    {isLoadingWallet ? (
                      <div className="p-4 bg-white border border-gray-200 rounded-md">
                        <div className="text-xs font-medium text-gray-700 mb-1">Balance</div>
                        <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ) : walletBalance > 0 ? (
                      <div className="p-4 bg-white border border-gray-200 rounded-md">
                        <div className="text-xs font-medium text-gray-700 mb-1">Balance</div>
                        <div className="text-2xl font-bold text-green-600">
                          ${walletBalance.toFixed(2)}
                        </div>
                      </div>
                    ) : null}

                    {/* Redeem Gift Card Form */}
                    <div className="p-4 bg-white border border-gray-200 rounded-md">
                      <label className="text-sm font-medium text-gray-900 block mb-2">
                        Redeem Gift Card
                      </label>
                      <form onSubmit={handleRedeemGiftCard} className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={giftCardCode}
                          onChange={(e) => {
                            setGiftCardCode(e.target.value.toUpperCase())
                            setRedeemResult(null)
                          }}
                          placeholder="Enter code..."
                          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          disabled={isRedeeming}
                        />
                        <button
                          type="submit"
                          disabled={!giftCardCode.trim() || isRedeeming}
                          className="px-4 py-2 bg-pink-500 text-white text-sm font-medium rounded-md hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex-shrink-0"
                        >
                          {isRedeeming ? 'Redeeming...' : 'Redeem'}
                        </button>
                      </form>

                      {redeemResult && (
                        <div className={`mt-3 p-3 rounded-md text-sm ${
                          redeemResult.success
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {redeemResult.message}
                        </div>
                      )}
                    </div>

                    {/* Saved Cards */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-900">Saved Cards</h3>
                        <button
                          onClick={() => setIsAddModalOpen(true)}
                          className="px-3 py-1 text-xs font-medium text-white rounded-md transition-colors"
                          style={{background: 'linear-gradient(135deg, #ffc9e3 0%, #ffb3d9 100%)'}}
                        >
                          + Add Card
                        </button>
                      </div>

                      {isLoadingCards ? (
                        <div className="text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-pink-500"></div>
                        </div>
                      ) : paymentMethods.length === 0 ? (
                        <div className="text-center py-4 bg-white border border-gray-200 rounded-md">
                          <p className="text-sm text-gray-800">No saved cards</p>
                          <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="mt-2 text-xs text-pink-600 hover:text-pink-700"
                          >
                            Add your first card
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {paymentMethods.map((pm) => (
                            <PaymentMethodCard
                              key={pm.id}
                              paymentMethod={pm}
                              onSetDefault={handleSetDefault}
                              onDelete={handleDeleteCard}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Security info */}
                    {paymentMethods.length > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex gap-2">
                          <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <p className="text-xs text-blue-800">
                            Your payment info is encrypted and securely stored by Stripe.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Section - only for identity-verified users */}
          {user && isIdentityVerified && !isLoadingDocuments && (
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  Documents
                </h2>

                <div className="space-y-2">
                  {/* NDA */}
                  <button
                    onClick={() => setOpenDocumentType(MASTER_AGREEMENT_TYPE)}
                    className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-900">NDA</span>
                    </div>
                    {isDocumentSigned(MASTER_AGREEMENT_TYPE) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Signed
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
                        Sign Now
                      </span>
                    )}
                  </button>

                  {/* Meetup Agreement */}
                  <button
                    onClick={() => setOpenDocumentType(MEETUP_AGREEMENT_TYPE)}
                    className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-900">Meetup Agreement</span>
                    </div>
                    {isDocumentSigned(MEETUP_AGREEMENT_TYPE) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Signed
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
                        Sign Now
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notification Preferences Section */}
          {user && (
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  Notification Preferences
                </h2>
                <p className="text-xs text-gray-600 mb-4">
                  Choose how you want to be notified when you receive new messages
                </p>

                {/* Email Notifications Toggle */}
                <div className="flex items-center justify-between mb-3 p-3 bg-white border border-gray-200 rounded-md">
                  <div>
                    <label className="text-sm font-medium text-gray-900 block">
                      Email Notifications
                    </label>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Get notified via email when you receive messages
                    </p>
                  </div>
                  <button
                    onClick={() => handleTogglePreference('email', !emailNotifications)}
                    disabled={isSavingPreferences}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      emailNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    } ${isSavingPreferences ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailNotifications ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* SMS Notifications Toggle */}
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md">
                  <div>
                    <label className="text-sm font-medium text-gray-900 block">
                      SMS Notifications
                    </label>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Get notified via text message when you receive messages
                    </p>
                  </div>
                  <button
                    onClick={() => handleTogglePreference('sms', !smsNotifications)}
                    disabled={isSavingPreferences}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      smsNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    } ${isSavingPreferences ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        smsNotifications ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Section */}
          {user && <SubscriptionSection userId={user.id} navigate={navigate} />}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full py-3 bg-red-300 text-red-900 rounded-lg font-medium shadow-sm hover:bg-red-400 transition-colors"
            >
              Logout
            </button>

            {/* Back Button */}
            <button
              onClick={() => navigate('/welcome')}
              className="w-full py-3 bg-gray-200 text-gray-900 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Envelope Modal (for adding address when none exists) */}
      <EnvelopeModal
        isOpen={showEnvelopeModal}
        onClose={() => setShowEnvelopeModal(false)}
        onAddressSaved={refreshMailing}
      />

      {/* Edit Address: First Confirmation */}
      <Modal
        isOpen={showEditConfirm}
        onClose={() => setShowEditConfirm(false)}
        title="Edit Address"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Are you sure you want to change your mailing address? Your new address will need to be verified.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowEditConfirm(false)}
              className="flex-1 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmEdit}
              className="flex-1 py-2 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors"
            >
              Yes, Edit Address
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Address: Form */}
      <Modal
        isOpen={showEditForm}
        onClose={() => { setShowEditForm(false); setAddressUpdateError(null) }}
        title="Update Address"
      >
        {addressUpdateError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {addressUpdateError}
          </div>
        )}
        <AddressForm
          initialData={mailingAddress ? {
            full_name: mailingAddress.full_name,
            address_line1: mailingAddress.address_line1,
            address_line2: mailingAddress.address_line2 || '',
            city: mailingAddress.city,
            state: mailingAddress.state,
            zip_code: mailingAddress.zip_code
          } : undefined}
          onSubmit={handleEditFormSubmit}
          onCancel={() => { setShowEditForm(false); setAddressUpdateError(null) }}
          submitLabel="Verify New Address"
          isSubmitting={false}
        />
      </Modal>

      {/* Edit Address: Second Confirmation */}
      <Modal
        isOpen={showUpdateConfirm}
        onClose={() => { setShowUpdateConfirm(false); setPendingUpdate(null) }}
        title="Confirm Address Change"
      >
        <div className="space-y-4">
          {mailingAddress && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-1">Current Address</p>
              <p className="text-sm text-gray-700">{mailingAddress.full_name}</p>
              <p className="text-sm text-gray-700">{mailingAddress.address_line1}, {mailingAddress.city}, {mailingAddress.state} {mailingAddress.zip_code}</p>
            </div>
          )}
          {pendingUpdate && (
            <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
              <p className="text-xs font-medium text-pink-600 mb-1">New Address</p>
              <p className="text-sm text-gray-900">{pendingUpdate.full_name}</p>
              <p className="text-sm text-gray-700">{pendingUpdate.address_line1}</p>
              {pendingUpdate.address_line2 && <p className="text-sm text-gray-700">{pendingUpdate.address_line2}</p>}
              <p className="text-sm text-gray-700">{pendingUpdate.city}, {pendingUpdate.state} {pendingUpdate.zip_code}</p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => { setShowUpdateConfirm(false); setPendingUpdate(null) }}
              className="flex-1 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isUpdatingAddress}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmUpdate}
              className="flex-1 py-2 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50"
              disabled={isUpdatingAddress}
            >
              {isUpdatingAddress ? 'Updating...' : 'Confirm Change'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddCardSuccess}
      />

      {/* Document Signing Modals */}
      <DocumentSigningModal
        isOpen={openDocumentType === MASTER_AGREEMENT_TYPE}
        onClose={() => setOpenDocumentType(null)}
        onSigned={refreshDocumentStatus}
        documentType={MASTER_AGREEMENT_TYPE}
        documentVersion={MASTER_AGREEMENT_VERSION}
        title={MASTER_AGREEMENT_TITLE}
        sections={MASTER_AGREEMENT_SECTIONS}
      />
      <DocumentSigningModal
        isOpen={openDocumentType === MEETUP_AGREEMENT_TYPE}
        onClose={() => setOpenDocumentType(null)}
        onSigned={refreshDocumentStatus}
        documentType={MEETUP_AGREEMENT_TYPE}
        documentVersion={MEETUP_AGREEMENT_VERSION}
        title={MEETUP_AGREEMENT_TITLE}
        sections={MEETUP_AGREEMENT_SECTIONS}
      />
    </div>
  )
}

/**
 * PlanSelectionModal - Simple modal for choosing a subscription plan
 */
interface PlanSelectionModalProps {
  onClose: () => void
  onSelectPlan: (priceId: string) => void
}

function PlanSelectionModal({ onClose }: PlanSelectionModalProps) {
  const { user } = useAuth()
  const [plans, setPlans] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  useEffect(() => {
    fetchPlans()
  }, [])

  const handleSelectPlan = (priceId: string) => {
    setSelectedPriceId(priceId)
    setShowPaymentModal(true)
  }

  const fetchPlans = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/subscriptions/plans`)

      if (response.ok) {
        const data = await response.json()
        setPlans(data.plans || [])
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Plan</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => (
                <div key={plan.id} className="border border-gray-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {getPlanDisplayName(plan.name)}
                  </h3>

                  {/* Monthly Option */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="font-semibold text-gray-900">Monthly Plan</p>
                        <p className="text-sm text-gray-600">Pay month by month</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">${plan.monthly_price}</p>
                        <p className="text-sm text-gray-600">/month</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSelectPlan(plan.stripe_price_id_monthly)}
                      className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                    >
                      Select Monthly
                    </button>
                  </div>

                  {/* Annual Option */}
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="font-semibold text-gray-900">Annual Plan</p>
                        <p className="text-sm text-gray-600">Best value - save ${(plan.monthly_price * 12) - plan.annual_price}/year</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">${plan.annual_price}</p>
                        <p className="text-sm text-gray-600">/year</p>
                        <p className="text-xs text-blue-600 font-medium">${(plan.annual_price / 12).toFixed(2)}/mo</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSelectPlan(plan.stripe_price_id_annual)}
                      className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                    >
                      Select Annual (Save ${(plan.monthly_price * 12) - plan.annual_price})
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPriceId && user && (
        <SubscriptionOnboardingModal
          isOpen={showPaymentModal}
          userId={user.id}
          inviteToken={null}
          onComplete={() => {
            setShowPaymentModal(false)
            onClose()
            window.location.reload() // Reload to show new subscription
          }}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
    </>
  )
}

// Helper functions for subscription display
const getStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    'trialing': 'Intro Period',
    'active': 'Active',
    'canceled': 'Cancelled',
    'past_due': 'Past Due',
    'incomplete': 'Processing'
  }
  return statusMap[status] || status
}

const formatDate = (isoString: string): string => {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC'
  })
}

const getPlanDisplayName = (planName: string): string => {
  if (planName?.toLowerCase().includes('standard')) {
    return 'Full Private Access'
  }
  return planName || 'Full Private Access'
}

/**
 * SubscriptionSection - Simple subscription status display
 * Shows current subscription or prompts to subscribe
 * Also handles grandfathered users with premium_until access
 */
function SubscriptionSection({ userId, navigate }: { userId: string, navigate: any }) {
  const [subscription, setSubscription] = useState<any>(null)
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showPlanModal, setShowPlanModal] = useState(false)

  useEffect(() => {
    fetchSubscription()
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/api/customer/premium-status/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        // premium-status returns { hasPremium, premiumUntil, source, ... }
        setProfile({
          premium_until: data.premiumUntil || data.premium_until || null
        })
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    }
  }

  const fetchSubscription = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/api/subscriptions/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setSubscription(data.subscription)
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error)
    } finally {
      setLoading(false)
    }
  }

  // Calculate days remaining for grandfathered users
  const calculateDaysRemaining = (premiumUntil: string): number => {
    const endDate = new Date(premiumUntil)
    const today = new Date()
    const diffTime = endDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  // Check if user is grandfathered (has premium_until but no subscription)
  const isGrandfathered = !subscription && profile?.premium_until &&
    new Date(profile.premium_until) > new Date()

  if (loading) {
    return (
      <div className="mb-6 bg-gray-50 rounded-lg p-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-3 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">
          Subscription
        </h2>

        {subscription ? (
          <div className="bg-white border border-gray-200 rounded-md p-3 mb-3">
            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  subscription.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : subscription.status === 'trialing'
                    ? 'bg-blue-100 text-blue-800'
                    : subscription.status === 'incomplete'
                    ? 'bg-purple-100 text-purple-800'
                    : subscription.status === 'past_due'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {subscription.status === 'trialing' ? '🎉 ' : ''}
                {getStatusDisplay(subscription.status)}
              </span>
              <span className="text-sm font-medium text-gray-900">
                {getPlanDisplayName(subscription.plan?.name)} - {subscription.plan_type === 'monthly' ? 'Monthly' : 'Annual'}
              </span>
            </div>

            {/* Trial End, Next Billing Date, or Access End Info */}
            {subscription.status === 'trialing' && subscription.trial_end ? (
              <p className="text-xs text-gray-600 mb-3">
                ✨ Trial ends: {formatDate(subscription.trial_end)}
                <span className="text-gray-500"> (then ${subscription.amount}/{subscription.plan_type === 'monthly' ? 'mo' : 'yr'})</span>
              </p>
            ) : subscription.status === 'incomplete' ? (
              <p className="text-xs text-gray-600 mb-3">
                ⏳ Payment processing... Your subscription will activate shortly
              </p>
            ) : subscription.status === 'canceled' || subscription.cancel_at_period_end ? (
              <p className="text-xs text-gray-600 mb-3">
                Access ends: {formatDate(subscription.current_period_end)}
              </p>
            ) : (
              <p className="text-xs text-gray-600 mb-3">
                Next billing: {formatDate(subscription.current_period_end)}
              </p>
            )}

            {/* Manage Button */}
            <button
              onClick={() => navigate('/subscription')}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Manage Subscription
            </button>
          </div>
        ) : isGrandfathered ? (
          <div className="bg-white border border-gray-200 rounded-md p-3 mb-3">
            {/* Grandfathered User Status */}
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                🎉 Intro Period
              </span>
              <span className="text-sm font-medium text-gray-900">
                Full Private Access
              </span>
            </div>

            {/* Days Remaining */}
            <p className="text-xs text-gray-600 mb-3">
              ✨ Access ends: {formatDate(profile.premium_until)}
              <span className="block mt-1 font-medium text-blue-600">
                {calculateDaysRemaining(profile.premium_until)} days remaining
              </span>
              <span className="block mt-1 text-gray-500">
                Subscribe now to keep your access
              </span>
            </p>

            {/* Subscribe Button */}
            <button
              onClick={() => setShowPlanModal(true)}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Subscribe Now
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md p-3 mb-3">
            <p className="text-sm text-gray-600 mb-3">No active subscription</p>
            <button
              onClick={() => setShowPlanModal(true)}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              View Plans
            </button>
          </div>
        )}

        {/* Simple Plan Selection Modal */}
        {showPlanModal && (
          <PlanSelectionModal
            onClose={() => setShowPlanModal(false)}
            onSelectPlan={() => {
              // This callback is not used anymore since the modal handles everything internally
              setShowPlanModal(false)
            }}
          />
        )}
      </div>
    </div>
  )
}
